import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axiosInstance from '@/api/axios';

const LoginPage = () => {
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!email.endsWith('@datamatrixtech.com')) {
      toast.error('Only @datamatrixtech.com email addresses are allowed');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post('/auth/send-otp', { email });
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/verify-otp', { email, code: otp });
      toast.success('Login successful!');
      login(response.data.email, response.data.token);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758759637109-c752cb037b12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjB3aGl0ZSUyMGdlb21ldHJpYyUyMGJhY2tncm91bmQlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzcyODkyNDIyfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#312E81]/90 to-[#4F46E5]/80"></div>
        <div className="relative z-10 flex flex-col justify-center items-start p-16 text-white">
          <h1 className="text-5xl font-semibold tracking-tight leading-none mb-6">
            DataMatrix<br />Scribe Dashboard
          </h1>
          <p className="text-lg leading-relaxed opacity-90 max-w-md">
            Streamline your medical scribe workflow with efficient time tracking and comprehensive project management.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-12">
            <h2 className="text-3xl font-medium tracking-tight mb-2">Welcome Back</h2>
            <p className="text-sm text-muted-foreground">
              {step === 'email' 
                ? 'Enter your DataMatrix email to receive a login code'
                : 'Enter the 6-digit code sent to your email'}
            </p>
          </div>

          {step === 'email' ? (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="email-input"
                    id="email"
                    type="email"
                    placeholder="yourname@datamatrixtech.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-sm border-border"
                    required
                  />
                </div>
              </div>

              <Button
                data-testid="send-otp-button"
                type="submit"
                className="w-full h-12 rounded-sm bg-primary hover:bg-primary/90 shadow-sm transition-transform active:scale-95"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Login Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  One-Time Password
                </Label>
                <div className="flex justify-center">
                  <InputOTP
                    data-testid="otp-input"
                    maxLength={6}
                    value={otp}
                    onChange={(value) => setOtp(value)}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="h-14 w-12 rounded-sm border-border" />
                      <InputOTPSlot index={1} className="h-14 w-12 rounded-sm border-border" />
                      <InputOTPSlot index={2} className="h-14 w-12 rounded-sm border-border" />
                      <InputOTPSlot index={3} className="h-14 w-12 rounded-sm border-border" />
                      <InputOTPSlot index={4} className="h-14 w-12 rounded-sm border-border" />
                      <InputOTPSlot index={5} className="h-14 w-12 rounded-sm border-border" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Code expires in 5 minutes
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  data-testid="verify-otp-button"
                  type="submit"
                  className="w-full h-12 rounded-sm bg-primary hover:bg-primary/90 shadow-sm transition-transform active:scale-95"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </Button>
                
                <Button
                  data-testid="back-button"
                  type="button"
                  variant="ghost"
                  className="w-full h-12 rounded-sm"
                  onClick={() => {
                    setStep('email');
                    setOtp('');
                  }}
                >
                  Back to Email
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
